import React, { useState, useRef, useEffect } from "react";
import { BiFilterAlt } from "react-icons/bi";
import SearchPopup from "./SearchPopup";
import { RiSearchLine } from "react-icons/ri";
import Breadcrumbs from "../breadcrumbs/Breadcrumbs";
import { MdKeyboardArrowDown, MdKeyboardArrowUp } from "react-icons/md";
import { FiHeart, FiShoppingCart, FiEye, FiTrash2 } from "react-icons/fi";
import { FaHeart } from "react-icons/fa";
import { toast } from "react-hot-toast";
import { Link, useParams } from "react-router-dom";
import FilterBox from "./FilterBox";
import apiClient from "../../services/apiClient";
import { IMAGE_PATH } from "../../utils/constants";
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import { useDispatch, useSelector } from "react-redux";
import { setUser } from "../../features/auth/authSlice";
import { addToCart as addToCartAction, removeFromCart } from "../../features/cart/cartSlice";
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';

const Product = () => {
  const [showSearch, setShowSearch] = useState(false);
  const [hoveredProduct, setHoveredProduct] = useState(null);
  const [imageHovered, setImageHovered] = useState(null);
  const [sortDropdownOpen, setSortDropdownOpen] = useState(false);
  const [currentSort, setCurrentSort] = useState("Newest");
  const [filterOpen, setFilterOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedKeywords, setSelectedKeywords] = useState([]);
  const [priceRange, setPriceRange] = useState([0, 10000]);
  const [sortField, setSortField] = useState("created_at");
  const [sortOrder, setSortOrder] = useState("desc");
  const { catid } = useParams();

  
  const dropdownRef = useRef(null);
  const user = useSelector(state => state.auth.user);
  const cart = useSelector(state => state.cart.items);
  const breadcrumbItems = [{ label: "Home", url: "/" }, { label: "Products" }];
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  useEffect(() => {
    setSelectedCategories([catid]);
  },[catid])

  const sortOptions = [
    "Newest",
    "Price, low to high",
    "Price, high to low",
    "Alphabetically, A-Z",
    "Alphabetically, Z-A",
    "Most Bought",
  ];

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    error,
    refetch,
  } = useInfiniteQuery({
    queryKey: ['products', searchTerm, selectedCategories, selectedKeywords, priceRange, sortField, sortOrder, user?.id],
    queryFn: async ({ pageParam = 1 }) => {
      const response = await apiClient.get("/products-list", {
        params: {
          search: searchTerm,
          categories: selectedCategories.join(','),
          keywords: selectedKeywords.join(','),
          price_min: priceRange[0],
          price_max: priceRange[1],
          sort_by: sortField,
          sort_order: sortOrder,
          user_id: user ? user.id : null,
          page: pageParam,
          per_page: 40
        }
      });

      // Process the products
      const processedProducts = response.data.data?.map((item) => {
        if (item.is_combo) {
          return {
            ...item,
            is_combo: true,
            name: item.title || item.name,
            sale_price: item.combo_price || item.sale_price,
            price: item.price || (item.products ? item.products.reduce(
              (total, product) => total + parseFloat(product.price || 0),
              0
            ) : 0),
            media: item.image_path ? [{ file_path: item.image_path, file_type: "image" }] :
              (item.media || []),
          };
        } else {
          return {
            ...item,
            is_combo: false,
            media: item.media || [],
          };
        }
      }) || [];

      return {
        products: processedProducts,
        pagination: response.data.pagination,
        nextPage: response.data.pagination?.current_page < response.data.pagination?.last_page
          ? response.data.pagination.current_page + 1
          : undefined
      };
    },
    getNextPageParam: (lastPage) => lastPage.nextPage,
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 10 * 60 * 1000, // 10 minutes
  });

  // Flatten all products from all pages
  const allProducts = data?.pages.flatMap(page => page.products) || [];

  // Show error toast if fetch fails
  React.useEffect(() => {
    if (error) {
      console.error("Error fetching products:", error);
      toast.error("Failed to load products");
    }
  }, [error]);

  // Refetch when filters change
  React.useEffect(() => {
    localStorage.removeItem('redirecturl');
    // React Query will automatically refetch when queryKey changes
  }, [searchTerm, selectedCategories, selectedKeywords, priceRange, sortField, sortOrder, user]);

  // Toggle wishlist item
  const toggleWishlist = (product) => {
    if (user) {
      const payload = {
        product_id: product.id,
        user_id: user.id,
        product_type: product.is_combo ? "combo" : "normal",
      };

      apiClient.post("/wishlist", payload)
        .then((res) => {
          toast.success(res.data.message || "Wishlist updated!");
          queryClient.invalidateQueries({ queryKey: ['products'] });
        })
        .catch((err) => {
          console.error("Wishlist error", err);
          toast.error("Failed to update wishlist.");
        });
    } else {
      toast.error("Please login to manage your wishlist.");
    }
  };

  const toggleCart = (product) => {
    const existingProduct = cart.some((p) => p.id === product.id);
    if (!existingProduct) {
      dispatch(addToCartAction({ ...product, quantity: 1 }));
      toast.success("Added to cart");
    } else {
      dispatch(removeFromCart(product.id));
      toast.success("Removed from cart");
    }
  };

  const handleSortChange = (sortOption) => {
    setCurrentSort(sortOption);
    setSortDropdownOpen(false);

    switch (sortOption) {
      case "Newest":
        setSortField("created_at");
        setSortOrder("desc");
        break;
      case "Price, low to high":
        setSortField("sale_price");
        setSortOrder("asc");
        break;
      case "Price, high to low":
        setSortField("sale_price");
        setSortOrder("desc");
        break;
      case "Alphabetically, A-Z":
        setSortField("name");
        setSortOrder("asc");
        break;
      case "Alphabetically, Z-A":
        setSortField("name");
        setSortOrder("desc");
        break;
      case "Most Bought":
        setSortField("popularity");
        setSortOrder("desc");
        break;
      default:
        setSortField("created_at");
        setSortOrder("desc");
    }
  };

  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setSortDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const loadMoreProducts = () => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  };

  const getProductImage = (product) => {
    if (product.media?.length > 0) {
      return `${IMAGE_PATH}${product.media[0].file_path}`;
    }
    return "";
  };

  const getSecondProductImage = (product) => {
    for (let x = 1 ; x <= product.media.length ; x++) {
      
      const imageTypes = ['image/jpeg','image/png','image/webp'];
      if (imageTypes.includes(product.media[x]?.file_type)) {
        return `${IMAGE_PATH}${product.media[x]?.file_path}`;
      }
    }
    return getProductImage(product);
  };


  const ProductSkeleton = () => (
    <div className='ProductBox'>
      <div className="productLink">
        <div className='ProductImage'>
          <Skeleton height="100%" />
        </div>
        <div className="ProductText">
          <h3><Skeleton width={120} /></h3>
          <div className="priceContainer">
            <p className="originalPrice"><Skeleton width={60} /></p>
            <span className="offerPrice"><Skeleton width={50} /></span>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div id="Product">
      <div className="BreadCrumbsBox">
        <div className="container">
          <Breadcrumbs items={breadcrumbItems} />
        </div>
      </div>
      <div className="container">
        <div className="ProductMain">
          <div className="ProductFilterSort">
            <div className="ProductFilterSearch">
              <div className="FilterBox" onClick={() => setFilterOpen(true)}>
                <BiFilterAlt />
                <p>Filters</p>
              </div>
              <div className="SearchBox">
                <button
                  onClick={() => setShowSearch(true)}
                  className="searchBtn"
                >
                  <RiSearchLine />
                </button>
              </div>
            </div>
            <div className="ProductSortBox">
              <div className="ProductSortText">
                <p>Sort by:</p>
              </div>
              <div className='ProductSortDropBox' ref={dropdownRef}>
                <div
                  className='SortDropTextIcon'
                  onClick={(e) => {
                    e.stopPropagation();
                    setSortDropdownOpen(!sortDropdownOpen);
                  }}
                >
                  <p>{currentSort}</p>
                  {sortDropdownOpen ? <MdKeyboardArrowUp /> : <MdKeyboardArrowDown />}
                </div>
                {sortDropdownOpen && (
                  <div
                    className='ProductDropdownBox'
                    onClick={(e) => e.stopPropagation()}
                  >
                    <ul>
                      {sortOptions.map((option) => (
                        <li
                          key={option}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSortChange(option);
                          }}
                        >
                          {option}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="homProductCardList">
            {isLoading ? (
              Array(8).fill(0).map((_, index) => (
                <ProductSkeleton key={`skeleton-${index}`} />
              ))
            ) : allProducts.length > 0 ? (
              allProducts.map((product, index) => (
                <div
                  className="ProductBox"
                  key={`${product.id}-${product.is_combo ? 'combo' : 'normal'}-${index}`}
                  onMouseEnter={() => setHoveredProduct(product.id)}
                  onMouseLeave={() => {
                    setHoveredProduct(null);
                    setImageHovered(null);
                  }}
                >
                  <Link
                    to={!product.is_combo ? `/products/${product.id}` : `/combo/${product.id}`}
                    className="productLink"
                    state={{ product }}
                  >
                    <div
                      className="ProductImage"
                      onMouseEnter={() => setImageHovered(product.id)}
                      onMouseLeave={() => {
                        if (hoveredProduct !== product.id) {
                          setImageHovered(null);
                        }
                      }}
                    >
                      <img
                        src={imageHovered === product.id ? getSecondProductImage(product) : getProductImage(product)}
                        alt={product.name}
                        loading="lazy"
                        className={imageHovered === product.id ? "fade-in" : "fade-in"}
                        onLoad={(e) => {
                          e.target.classList.add('loaded');
                          if (imageHovered === product.id) {
                            e.target.classList.add('fade-in');
                          }
                        }}
                      />

                      {!imageHovered && (
                        <div className="imagePlaceholder">
                          <Skeleton height="100%" width="100%" />
                        </div>
                      )}
                    </div>
                    <div className="ProductText">
                      <h3>{product.name}</h3>
                      <div className="priceContainer">
                        {product.price && (
                          <p className="originalPrice">₹{product.price}</p>
                        )}
                        {product.sale_price && (
                          <span className="offerPrice">₹{product.sale_price}</span>
                        )}
                      </div>
                    </div>
                  </Link>

                  <div
                    className={`productIcons ${hoveredProduct === product.id ? "visible" : ""
                      }`}
                    onMouseEnter={(e) => {
                      e.stopPropagation();
                      setHoveredProduct(product.id);
                    }}
                  >
                    <div
                      className="iconBox"
                      data-tooltip={
                        product.is_fav
                          ? "Remove from wishlist"
                          : "Add to wishlist"
                      }
                      onClick={(e) => {
                        e.preventDefault();
                        toggleWishlist(product);
                      }}
                    >
                      {product.is_fav ? (
                        <FaHeart className="icon" style={{ color: "red" }} />
                      ) : (
                        <FiHeart className="icon" />
                      )}
                    </div>

                    <div
                      className="iconBox"
                      data-tooltip={cart.some(item => item.id === product.id)
                        ? "Remove from cart"
                        : "Add to cart"
                      }
                      onClick={(e) => {
                        e.preventDefault();
                        toggleCart(product);
                      }}
                      aria-label={cart.some(item => item.id === product.id)
                        ? "Remove from cart"
                        : "Add to cart"
                      }
                    >
                      {cart.some(item => item.id === product.id) ? (
                        <FiTrash2 className="icon" />
                      ) : (
                        <FiShoppingCart className="icon" />
                      )}
                    </div>

                    <div className="iconBox" data-tooltip="Quick View">
                      <FiEye className="icon" />
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="no-products-found">
                <p>No products found matching your criteria</p>
              </div>
            )}
          </div>

          {hasNextPage && (
            <div className="LoadmoreBtn">
              <button
                onClick={loadMoreProducts}
                disabled={isFetchingNextPage}
              >
                {isFetchingNextPage ? 'Loading...' : 'Load More'}
              </button>
            </div>
          )}
        </div>
      </div>

      <SearchPopup
        isOpen={showSearch}
        onClose={() => setShowSearch(false)}
        onSearch={(term) => {
          setSearchTerm(term);
          setShowSearch(false);
        }}
      />

      <FilterBox
        isOpen={filterOpen}
        onClose={() => setFilterOpen(false)}
        onFilter={(filters) => {
          setSelectedCategories(filters.categories || []);
          setSelectedKeywords(filters.keywords || []);
          setPriceRange(filters.priceRange || [0, 10000]);
          setFilterOpen(false);
        }}
      />
    </div>
  );
};

export default Product;